import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "./ui/command";
import { TrendingUp, Coins, Layers, DollarSign } from "lucide-react";
import api from "../lib/api";

export default function SearchCommand({ open, setOpen }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancel = false;
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(query)}`)
        .then((res) => { if (!cancel) setResults(res.data.results || []); })
        .catch(() => { if (!cancel) setResults([]); });
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [query]);

  const select = (item) => {
    setOpen(false);
    setQuery("");
    navigate(`/asset/${item.asset_type}/${item.symbol}`);
  };

  const stocks = results.filter((r) => r.asset_type === "stock");
  const etfs = results.filter((r) => r.asset_type === "etf");
  const forex = results.filter((r) => r.asset_type === "forex");
  const crypto = results.filter((r) => r.asset_type === "crypto");

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Search assets" description="Search stocks, ETFs, commodities & crypto" shouldFilter={false}>
      <CommandInput
        placeholder="Search stocks, ETFs (SPY, QQQ, GLD), or crypto..."
        value={query}
        onValueChange={setQuery}
        data-testid="search-input"
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {stocks.length > 0 && (
          <CommandGroup heading="Stocks">
            {stocks.map((item) => (
              <CommandItem
                key={`stock-${item.symbol}`}
                value={`stock-${item.symbol}-${item.name}`}
                onSelect={() => select(item)}
                data-testid={`search-result-stock-${item.symbol}`}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-[#547A8F]/10 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-[#547A8F]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-mono-ticker font-semibold text-sm text-[#2A2F2B] dark:text-[#E6EDF3]">
                    {item.symbol}
                  </div>
                  <div className="text-xs text-[#6B7068]">{item.name}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[#6B7068]">
                  Stock
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {etfs.length > 0 && (
          <CommandGroup heading="ETFs & Commodities">
            {etfs.map((item) => (
              <CommandItem
                key={`etf-${item.symbol}`}
                value={`etf-${item.symbol}-${item.name}`}
                onSelect={() => select(item)}
                data-testid={`search-result-etf-${item.symbol}`}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-[#2EA44F]/15 flex items-center justify-center">
                  <Layers className="w-4 h-4 text-[#2EA44F]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-mono-ticker font-semibold text-sm text-[#2A2F2B] dark:text-[#E6EDF3]">
                    {item.symbol}
                  </div>
                  <div className="text-xs text-[#6B7068]">{item.name}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[#6B7068]">
                  ETF
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {crypto.length > 0 && (
          <CommandGroup heading="Crypto">
            {crypto.map((item) => (
              <CommandItem
                key={`crypto-${item.symbol}`}
                value={`crypto-${item.symbol}-${item.name}`}
                onSelect={() => select(item)}
                data-testid={`search-result-crypto-${item.symbol}`}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-[#D18E63]/10 flex items-center justify-center">
                  <Coins className="w-4 h-4 text-[#D18E63]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-mono-ticker font-semibold text-sm text-[#2A2F2B] dark:text-[#E6EDF3]">
                    {item.symbol}
                  </div>
                  <div className="text-xs text-[#6B7068]">{item.name}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[#6B7068]">
                  Crypto
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {forex.length > 0 && (
          <CommandGroup heading="Forex">
            {forex.map((item) => (
              <CommandItem
                key={`forex-${item.symbol}`}
                value={`forex-${item.symbol}-${item.name}`}
                onSelect={() => select(item)}
                data-testid={`search-result-forex-${item.symbol}`}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-[#547A8F]/10 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-[#547A8F]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-mono-ticker font-semibold text-sm text-[#2A2F2B] dark:text-[#E6EDF3]">
                    {item.symbol}
                  </div>
                  <div className="text-xs text-[#6B7068]">{item.name}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[#6B7068]">
                  Forex
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
